import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import {
    autoinject,
    bind,
    DisplayMapMarker,
    inject,
    ItemCarousel,
    Map,
    ModalHandle,
    softwareVersionToString,
    toArray,
} from 'Mri.Web.Common';
import * as React from 'react';
import { LinkContainer } from 'react-router-bootstrap';
import { AppStore } from '../Core/AppStore';
import { TicketModal } from '../Core/TicketModal';
import { ActiveDisplay } from './ActiveDisplay';
import { DisplayScreenSnapshot } from './DisplayScreenSnapshot';
import { DisplaysStore } from './DisplaysStore';

export interface IDisplayCardProps {
    onFavoritesClick?(display: ActiveDisplay): void;
    readonly display: ActiveDisplay;
}

@autoinject
@observer
export class DisplayCard extends React.Component<IDisplayCardProps> {
    public async componentDidMount() {
        try {
            const { display } = this.props;
            await this.displaysStore.loadLatestScreenSnapshotAsync(display.serialNumber);
        } catch (e: any) {
            console.error(e);
        }
    }

    public render() {
        const { display } = this.props;

        return (
            <div className="col-12 col-md-6 col-xl-4 my-3">
                <div className="card h-100">
                    <div className="container flex-grow-1">
                        <div className="row h-100 align-items-center">
                            {this.screenSnapshots &&
                                !this.viewMap &&
                                this.screenSnapshots.map(s => (
                                    <div
                                        key={s.uri}
                                        className={
                                            this.screenSnapshots?.length == 1
                                                ? 'col-12 text-center'
                                                : 'col-6 text-center mb-2'
                                        }
                                    >
                                        <DisplayScreenSnapshot key={s.uri} screenSnapshot={s} />
                                    </div>
                                ))}
                        </div>
                    </div>

                    <div className={this.screenSnapshots ? 'p-1' : 'p-1'}>
                        <div className="container-fluid">
                            <div className="row h-100">
                                <div className="col-12 p-2">
                                    <div>
                                        <h4 className="d-inline-block">
                                            <b>{display.serialNumber}</b>
                                        </h4>
                                    </div>

                                    <p className="mb-0">
                                        <strong>Display ID:</strong>{' '}
                                        {display.displayConnectionData?.customerDisplayID ?? '-'}
                                    </p>

                                    <p className="mb-0">
                                        <strong>Site ID:</strong> {display.unitLocation?.customerSiteID ?? '-'}
                                    </p>

                                    <p className="mb-0">
                                        <strong>Software Version:</strong>{' '}
                                        {softwareVersionToString(display.displaySoftware?.mriSoftwareVersion) ?? '-'}
                                    </p>

                                    <p className="mb-0">
                                        <strong>Display Type:</strong> {display.description ?? '-'}
                                    </p>

                                    <p className="mb-0">
                                        <strong>Status:</strong>{' '}
                                        <b className={this.statusClassName}>{display.statusMessageBrief ?? '-'}</b>
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="card-footer d-flex justify-content-around">
                        <a href={`/proxy/${display.serialNumber}`} target="_blank" className="btn btn-link">
                            StatusVu&reg;
                        </a>

                        <LinkContainer to={`/displays/${display.serialNumber}`}>
                            <a className="btn btn-primary">Details</a>
                        </LinkContainer>

                        <a className="btn btn-link" onClick={this.openTicketsModal}>
                            Create Ticket
                        </a>
                    </div>
                </div>

                <TicketModal modalHandle={this.ticketModalHandle} display={display} />
            </div>
        );
    }

    @computed
    private get screenSnapshots() {
        return this.displaysStore.screenSnapshots.get(this.props.display.serialNumber);
    }

    @computed
    private get statusClassName() {
        switch (this.props.display.state) {
            case 'good':
                return 'primary';
            case 'offline':
                return 'faded';
            case 'exception':
                return 'info';
            case 'warning':
                return 'warning';
            case 'critical':
                return 'danger';
        }
    }

    @bind
    private openTicketsModal(event: React.MouseEvent<HTMLAnchorElement>) {
        event.preventDefault();

        this.ticketModalHandle.open();
    }

    private ticketModalHandle = new ModalHandle();
    @observable
    private viewMap: boolean = false;
    @inject
    private displaysStore!: DisplaysStore;
}
